import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, Plus, Loader2, Trash2, LogOut, DollarSign } from 'lucide-react'
import { api } from '../../api/client'
import { useAuthStore } from '../../stores/authStore'
import { APP_NAME, DEFAULT_LARGE_TXN_THRESHOLD } from '../../constants'

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0)

interface Account { name: string; balance: number; org: string; id?: string; available_balance?: number }
interface Institution { name: string; accounts: Account[]; total_balance: number }
interface AccountsResp { accounts: Account[]; institutions: Institution[]; plaid: { connected: boolean; env: string } }

export function SettingsPage() {
  const navigate = useNavigate()
  const logout = useAuthStore(s => s.logout)
  const [acctData, setAcctData] = useState<AccountsResp | null>(null)
  const [loading, setLoading] = useState(true)
  const [linking, setLinking] = useState(false)
  const [error, setError] = useState('')
  const [threshold, setThreshold] = useState<number>(DEFAULT_LARGE_TXN_THRESHOLD)
  const [thresholdSaved, setThresholdSaved] = useState(false)

  const loadAccounts = async () => {
    try {
      const d = await api.get<AccountsResp>('/api/accounts')
      setAcctData(d)
    } catch { /* ignore */ }
    setLoading(false)
  }

  useEffect(() => { loadAccounts() }, [])

  // Load user preferences (large txn threshold)
  useEffect(() => {
    api.get<{ large_txn_threshold?: number }>('/api/preferences').then((prefs) => {
      if (prefs.large_txn_threshold != null) setThreshold(prefs.large_txn_threshold)
    }).catch(() => {})
  }, [])

  const saveThreshold = async () => {
    try {
      await api.post('/api/preferences', { large_txn_threshold: threshold })
      setThresholdSaved(true)
      setTimeout(() => setThresholdSaved(false), 2000)
    } catch (e: any) { setError(e.message) }
  }

  const openPlaidLink = async () => {
    setError(''); setLinking(true)
    try {
      const { link_token } = await api.post<{ link_token: string }>('/api/plaid/create-link-token')
      if (!link_token) { setError('Failed to get link token'); setLinking(false); return }
      if (!(window as any).Plaid) { setError('Plaid not loaded. Refresh the page.'); setLinking(false); return }
      const handler = (window as any).Plaid.create({
        token: link_token,
        onSuccess: async (public_token: string, metadata: any) => {
          try {
            const inst = metadata.institution || {}
            await api.post('/api/plaid/exchange-token', { public_token, institution_id: inst.institution_id || '', institution_name: inst.name || '' })
            setLinking(false)
            loadAccounts()
          } catch (e: any) { setError(e.message); setLinking(false) }
        },
        onExit: () => setLinking(false),
      })
      handler.open()
    } catch (e: any) { setError(e.message); setLinking(false) }
  }

  const deleteAccount = async (accountId: string, name: string) => {
    if (!confirm(`Delete "${name}" and all its transactions?`)) return
    try {
      await api.delete(`/api/accounts/${accountId}`)
      loadAccounts()
    } catch (e: any) { setError(e.message) }
  }

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-white">Settings</h2>

      {/* Why Self-Fi link */}
      <button onClick={() => navigate('/why-self-fi')}
        className="group flex w-full items-center gap-4 rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-4 text-left transition-all hover:bg-indigo-500/20">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white">
          <Sparkles className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-indigo-300">Why {APP_NAME}?</p>
          <p className="text-sm text-indigo-400/70">Learn what makes {APP_NAME} your best finance copilot</p>
        </div>
      </button>

      {/* Large Transaction Threshold (REQ-207) */}
      <section className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-6">
        <h3 className="mb-3 text-lg font-semibold text-white flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-amber-400" /> Alert Threshold
        </h3>
        <p className="mb-4 text-sm text-slate-400">
          Transactions above this amount will be flagged as &ldquo;large charges&rdquo; on your dashboard and activities.
        </p>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
            <input
              type="number"
              min={1}
              max={100000}
              step={1}
              value={threshold}
              onChange={(e) => setThreshold(Math.max(1, Math.min(100000, parseInt(e.target.value) || 1)))}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 pl-7 pr-4 py-2.5 text-sm text-white focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <button onClick={saveThreshold}
            className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-500 transition-colors">
            {thresholdSaved ? 'Saved!' : 'Save'}
          </button>
        </div>
      </section>

      {/* Linked Accounts */}
      <section className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Linked Accounts</h3>
          <button onClick={openPlaidLink} disabled={linking}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50">
            {linking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {linking ? 'Connecting...' : 'Add Account'}
          </button>
        </div>

        {error && <p className="mb-3 text-sm text-rose-400">{error}</p>}

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
        ) : acctData?.institutions?.length ? (
          <div className="space-y-4">
            {acctData.institutions.map((inst, ii) => (
              <div key={ii} className="rounded-lg border border-slate-700/50 overflow-hidden">
                <div className="flex items-center justify-between bg-slate-900/50 px-4 py-3">
                  <div>
                    <p className="font-semibold text-sm text-slate-200">{inst.name}</p>
                    <p className="text-xs text-slate-500">{inst.accounts.length} account{inst.accounts.length !== 1 ? 's' : ''}</p>
                  </div>
                  <p className="font-bold text-sm text-white">{fmt(inst.total_balance)}</p>
                </div>
                <div className="divide-y divide-slate-700/30">
                  {inst.accounts.map((a, ai) => (
                    <div key={ai} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-slate-200">{a.name}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className={`text-sm font-bold tabular-nums ${a.balance >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{fmt(a.balance)}</p>
                        {a.id && (
                          <button onClick={() => deleteAccount(a.id!, a.name)} className="text-slate-500 hover:text-rose-400 transition-colors" title="Delete account">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <p className="text-xs text-slate-500 text-center">
              Plaid: {acctData.plaid.connected ? 'Connected' : 'Not connected'} · {acctData.plaid.env}
            </p>
          </div>
        ) : (
          <div className="py-8 text-center">
            <p className="text-slate-500 text-sm mb-3">No accounts linked yet</p>
            <button onClick={openPlaidLink} className="text-sm font-medium text-indigo-400 hover:text-indigo-300">
              Connect your bank →
            </button>
          </div>
        )}
      </section>

      {/* Sign Out */}
      <section className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-6">
        <button onClick={logout}
          className="flex items-center gap-2 rounded-lg border border-rose-500/30 px-4 py-2 text-sm font-medium text-rose-400 hover:bg-rose-500/10 transition-colors">
          <LogOut className="h-4 w-4" /> Sign Out
        </button>
      </section>
    </div>
  )
}
